import React, { useState, useEffect, useRef, useMemo } from 'react';
import 'flag-icons/css/flag-icons.min.css';
import MonitorChart from "./MonitorChart";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Shield, ArrowLeft, Trash2, Lock, Unlock, Edit, Users, TrendingUp, Mail, Send, RefreshCw, Download, RotateCcw, CheckCircle, AlertCircle, FileText, Plus, GripVertical, Layers, Save, X, BarChart3, Share2 } from 'lucide-react';
import VideoAnalytics from './VideoAnalytics';
import AdminGeneralSettings from './AdminGeneralSettings';
import { Settings as SettingsIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { ChevronDown, Check } from 'lucide-react';
import AdminViralityConfig from './AdminViralityConfig';
import AdminReferralEmailTemplate from './AdminReferralEmailTemplate';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [antifraudStats, setAntifraudStats] = useState(null);
  const [antifraudSuspects, setAntifraudSuspects] = useState([]);
  const [antifraudLog, setAntifraudLog] = useState([]);
  const [antifraudLoading, setAntifraudLoading] = useState(false);
  const [antifraudReferrals, setAntifraudReferrals] = useState([]);
  const [antifraudReferralsTotal, setAntifraudReferralsTotal] = useState(0);
  const [unblockingRef, setUnblockingRef] = useState(null);
  const [monitorData, setMonitorData] = useState(null);
  const [monitorHistory, setMonitorHistory] = useState([]);
  const [monitorPeriod, setMonitorPeriod] = useState('1h');
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [bandwidthSetting, setBandwidthSetting] = useState(500);
  const [bandwidthInput, setBandwidthInput] = useState('500');
  const [bandwidthSaving, setBandwidthSaving] = useState(false);
  const monitorInterval = useRef(null); // 'users', 'score', 'winners', 'email', 'updates', 'sections', or 'objects'
  
  // Users tab state
  const [users, setUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 20;

  // Filter users by search query (matches email or full_name, case-insensitive)
  const filteredUsers = useMemo(() => {
    const q = (userSearchQuery || '').trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const email = (u.email || '').toLowerCase();
      const fullName = (u.full_name || '').toLowerCase();
      return email.includes(q) || fullName.includes(q);
    });
  }, [users, userSearchQuery]);
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at' or 'total_points'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  
  // Score tab state
  const [scoreConfig, setScoreConfig] = useState(null);
  const [loadingScore, setLoadingScore] = useState(false);
  const [editingScore, setEditingScore] = useState(false);
  const canvasRef = useRef(null);
  
  // Graph zoom and test state
  const [viewRange, setViewRange] = useState({ start: 0, end: 50000 });
  const [testDistance, setTestDistance] = useState('');
  const [testResult, setTestResult] = useState(null);
  
  // Winners tab state
  const [winnerConfig, setWinnerConfig] = useState(null);
  const [loadingWinners, setLoadingWinners] = useState(false);
  const [editingWinners, setEditingWinners] = useState(false);
  
  // Email settings tab state
  const [emailSettings, setEmailSettings] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Updates tab state
  const [updateSettings, setUpdateSettings] = useState(null);
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  const [editingUpdates, setEditingUpdates] = useState(false);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  
  // Info sections tab state
  const [infoSections, setInfoSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [newSection, setNewSection] = useState({ title: '', content: '' });
  const [versionHistory, setVersionHistory] = useState([]);
  const [updatingApp, setUpdatingApp] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateStage, setUpdateStage] = useState('');
  
  // Backups state
  const [temporaryBackups, setTemporaryBackups] = useState([]);
  const [permanentBackups, setPermanentBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [draggedBackup, setDraggedBackup] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');

  // Prompt Objects state (for AI image generation)
  const [promptObjects, setPromptObjects] = useState([]);
  const [aiPromptTemplate, setAiPromptTemplate] = useState(`Edit this section of a 360-degree panoramic photo by adding the selected objects.

CRITICAL PLACEMENT REQUIREMENTS:
1. DO NOT place any objects in the leftmost 5% of the image
2. DO NOT place any objects in the rightmost 5% of the image  
3. DO NOT place any objects in the top 5% of the image
4. DO NOT place any objects in the bottom 5% of the image
5. Place objects ONLY in the central 90% area of the image

REALISM REQUIREMENTS:
1. Objects must be placed ON THE GROUND or on realistic surfaces (tables, shelves, floor)
2. Objects must have correct proportions relative to the scene
3. Match the lighting, shadows, and perspective of the original scene
4. Objects should look natural and belong in the scene
5. Keep the original background completely intact
6. Make objects clearly visible and properly sized for the scene`);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [newObjectText, setNewObjectText] = useState('');
  const [editingObjectId, setEditingObjectId] = useState(null);
  const [editingObjectText, setEditingObjectText] = useState('');

  // Analytics tab state
  const [analyticsGames, setAnalyticsGames] = useState([]);
  const [analyticsPanoramas, setAnalyticsPanoramas] = useState([]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Доступ запрещен');
      navigate('/');
      return;
    }
    
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'score') {
      fetchScoreConfig();
    } else if (activeTab === 'winners') {
      fetchWinnerConfig();
    } else if (activeTab === 'email') {
      fetchEmailSettings();
    } else if (activeTab === 'updates') {
      fetchUpdateSettings();
      fetchVersionHistory();
      fetchBackups();
    } else if (activeTab === 'sections') {
      fetchInfoSections();
    } else if (activeTab === 'objects') {
      fetchPromptObjects();
      fetchAiPromptTemplate();
    } else if (activeTab === 'monitoring') {
      // Legacy id — redirect to combined analytics tab
      setActiveTab('analytics');
      return;
    } else if (activeTab === 'antifraud') {
      setAntifraudLoading(true);
      Promise.all([
        axios.get(`${API}/admin/antifraud/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/antifraud/suspects`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/antifraud/log?limit=30`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/antifraud/referrals?limit=200`, { headers: getAuthHeader() })
      ]).then(([statsRes, suspectsRes, logRes, refRes]) => {
        setAntifraudStats(statsRes.data);
        setAntifraudSuspects(suspectsRes.data);
        setAntifraudLog(logRes.data);
        setAntifraudReferrals(refRes.data?.events || []);
        setAntifraudReferralsTotal(refRes.data?.total_flagged || 0);
      }).catch(err => console.error('Antifraud fetch error:', err))
      .finally(() => setAntifraudLoading(false));
    } else if (activeTab === 'analytics') {
      // Combined Analytics + Monitoring tab
      fetchAnalyticsData();
      // Fetch monitoring data
      setMonitorLoading(true);
      axios.get(`${API}/admin/monitoring/realtime`, { headers: getAuthHeader() })
        .then(res => setMonitorData(res.data))
        .catch(err => console.error('Monitor error:', err));
      axios.get(`${API}/admin/monitoring/bandwidth-setting`, { headers: getAuthHeader() })
        .then(res => { setBandwidthSetting(res.data.max_bandwidth_mbps); setBandwidthInput(String(res.data.max_bandwidth_mbps)); })
        .catch(() => {});
      axios.get(`${API}/admin/monitoring/history?period=${monitorPeriod}`, { headers: getAuthHeader() })
        .then(res => setMonitorHistory(res.data?.data || []))
        .catch(err => console.error('History error:', err))
        .finally(() => setMonitorLoading(false));
      // Auto-refresh realtime every 10s
      if (monitorInterval.current) clearInterval(monitorInterval.current);
      monitorInterval.current = setInterval(() => {
        axios.get(`${API}/admin/monitoring/realtime`, { headers: getAuthHeader() })
          .then(res => setMonitorData(res.data))
          .catch(() => {});
      }, 10000);
    } else {
      if (monitorInterval.current) { clearInterval(monitorInterval.current); monitorInterval.current = null; }
    }
  }, [user, navigate, activeTab]);

  // Unblock a flagged referral event from the Antifraud tab
  const handleUnblockReferral = async (linkId, newUserId) => {
    setUnblockingRef(`${linkId}|${newUserId}`);
    try {
      const r = await axios.post(
        `${API}/admin/referrals/${linkId}/events/${newUserId}/unblock`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Разблокировано. Начислено отправителю: ${r.data?.awarded || 0}`);
      setAntifraudReferrals(arr => arr.filter(e => !(e.link_id === linkId && e.user_id === newUserId)));
      setAntifraudReferralsTotal(t => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка разблокировки');
    } finally {
      setUnblockingRef(null);
    }
  };

  // Draw score curve whenever config changes
  useEffect(() => {
    if (activeTab === 'score' && scoreConfig && canvasRef.current) {
      drawScoreCurve();
    }
  }, [scoreConfig, activeTab, viewRange, testResult]);

  // Add mouse wheel zoom support
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || activeTab !== 'score') return;

    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 1.2 : 0.8;
      const currentRange = viewRange.end - viewRange.start;
      const newRange = currentRange * delta;
      
      // Calculate center point
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const canvasWidth = canvas.width;
      const padding = 50;
      const graphWidth = canvasWidth - 2 * padding;
      
      if (mouseX >= padding && mouseX <= canvasWidth - padding) {
        const mouseRatio = (mouseX - padding) / graphWidth;
        const mouseDistance = viewRange.start + mouseRatio * currentRange;
        
        const newStart = Math.max(0, mouseDistance - mouseRatio * newRange);
        const newEnd = Math.min(scoreConfig?.zero_after_meters || 10000000, newStart + newRange);
        
        setViewRange({ start: newStart, end: newEnd });
      }
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [activeTab, viewRange, scoreConfig]);

  // Info Sections functions
  const fetchInfoSections = async () => {
    setLoadingSections(true);
    try {
      const response = await axios.get(`${API}/info-sections`);
      setInfoSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      toast.error('Ошибка загрузки разделов');
    } finally {
      setLoadingSections(false);
    }
  };

  const createInfoSection = async () => {
    if (!newSection.title.trim() || !newSection.content.trim()) {
      toast.error('Заполните название и описание');
      return;
    }
    try {
      await axios.post(`${API}/info-sections`, newSection, {
        headers: getAuthHeader()
      });
      toast.success('Раздел добавлен');
      setNewSection({ title: '', content: '' });
      fetchInfoSections();
    } catch (error) {
      console.error('Error creating section:', error);
      toast.error('Ошибка создания раздела');
    }
  };

  const updateInfoSection = async (sectionId, data) => {
    try {
      await axios.put(`${API}/info-sections/${sectionId}`, data, {
        headers: getAuthHeader()
      });
      toast.success('Раздел обновлён');
      setEditingSection(null);
      fetchInfoSections();
    } catch (error) {
      console.error('Error updating section:', error);
      toast.error('Ошибка обновления раздела');
    }
  };

  const deleteInfoSection = async (sectionId) => {
    if (!window.confirm('Удалить раздел?')) return;
    try {
      await axios.delete(`${API}/info-sections/${sectionId}`, {
        headers: getAuthHeader()
      });
      toast.success('Раздел удалён');
      fetchInfoSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Ошибка удаления раздела');
    }
  };


  // Analytics functions
  const fetchAnalyticsData = async () => {
    try {
      const [gamesRes, panoramasRes] = await Promise.all([
        axios.get(`${API}/games`),
        axios.get(`${API}/panoramas`)
      ]);
      setAnalyticsGames(gamesRes.data || []);
      setAnalyticsPanoramas(panoramasRes.data || []);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    }
  };

  // Prompt Objects functions
  const fetchPromptObjects = async () => {
    setLoadingObjects(true);
    try {
      const response = await axios.get(`${API}/prompt-objects`);
      setPromptObjects(response.data);
    } catch (error) {
      console.error('Error fetching prompt objects:', error);
      toast.error('Ошибка загрузки объектов');
    } finally {
      setLoadingObjects(false);
    }
  };

  const fetchAiPromptTemplate = async () => {
    try {
      const response = await axios.get(`${API}/ai-prompt-template`);
      if (response.data.template) {
        setAiPromptTemplate(response.data.template);
      }
    } catch (error) {
      console.error('Error fetching AI prompt template:', error);
    }
  };

  const saveAiPromptTemplate = async () => {
    try {
      await axios.put(`${API}/ai-prompt-template`, {
        template: aiPromptTemplate
      }, {
        headers: getAuthHeader()
      });
      toast.success('Промпт сохранён');
    } catch (error) {
      console.error('Error saving AI prompt template:', error);
      toast.error('Ошибка сохранения промпта');
    }
  };

  const addPromptObject = async () => {
    if (!newObjectText.trim()) {
      toast.error('Введите текст объекта');
      return;
    }
    try {
      await axios.post(`${API}/prompt-objects`, { text: newObjectText }, {
        headers: getAuthHeader()
      });
      setNewObjectText('');
      toast.success('Объект добавлен');
      fetchPromptObjects();
    } catch (error) {
      console.error('Error adding object:', error);
      toast.error('Ошибка добавления объекта');
    }
  };

  const updatePromptObject = async (objectId) => {
    if (!editingObjectText.trim()) {
      toast.error('Введите текст объекта');
      return;
    }
    try {
      await axios.put(`${API}/prompt-objects/${objectId}`, { text: editingObjectText }, {
        headers: getAuthHeader()
      });
      setEditingObjectId(null);
      setEditingObjectText('');
      toast.success('Объект обновлён');
      fetchPromptObjects();
    } catch (error) {
      console.error('Error updating object:', error);
      toast.error('Ошибка обновления объекта');
    }
  };

  const deletePromptObject = async (objectId) => {
    if (!window.confirm('Удалить объект?')) return;
    try {
      await axios.delete(`${API}/prompt-objects/${objectId}`, {
        headers: getAuthHeader()
      });
      toast.success('Объект удалён');
      fetchPromptObjects();
    } catch (error) {
      console.error('Error deleting object:', error);
      toast.error('Ошибка удаления объекта');
    }
  };

  const startEditingObject = (obj) => {
    setEditingObjectId(obj.id);
    setEditingObjectText(obj.text);
  };

  const cancelEditingObject = () => {
    setEditingObjectId(null);
    setEditingObjectText('');
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Ошибка загрузки пользователей');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchScoreConfig = async () => {
    setLoadingScore(true);
    try {
      const response = await axios.get(`${API}/score/config`, {
        headers: getAuthHeader()
      });
      setScoreConfig(response.data);
    } catch (error) {
      console.error('Error fetching score config:', error);
      toast.error('Ошибка загрузки конфигурации очков');
    } finally {
      setLoadingScore(false);
    }
  };

  const handleRoleChange = async (userId, currentRole) => {
    const roles = ['user', 'editor', 'admin'];
    const currentIndex = roles.indexOf(currentRole);
    const newRole = roles[(currentIndex + 1) % roles.length];

    setActionLoading(userId);
    try {
      await axios.put(
        `${API}/users/${userId}`,
        { role: newRole },
        { headers: getAuthHeader() }
      );
      toast.success(`Роль изменена на ${newRole}`);
      fetchUsers();
    } catch (error) {
      console.error('Error changing role:', error);
      toast.error(error.response?.data?.detail || 'Ошибка изменения роли');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlockToggle = async (userId, currentBlocked) => {
    setActionLoading(userId);
    try {
      await axios.put(
        `${API}/users/${userId}/block?blocked=${!currentBlocked}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(currentBlocked ? 'Пользователь разблокирован' : 'Пользователь заблокирован');
      fetchUsers();
    } catch (error) {
      console.error('Error toggling block:', error);
      toast.error(error.response?.data?.detail || 'Ошибка блокировки');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId, userEmail) => {
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя ${userEmail}?`)) {
      return;
    }

    setActionLoading(userId);
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success('Пользователь удален');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    } finally {
      setActionLoading(null);
    }
  };

  const handleIntervalChange = (index, field, value) => {
    const newConfig = { ...scoreConfig };
    newConfig.intervals[index][field] = parseFloat(value) || 0;
    setScoreConfig(newConfig);
  };

  const handleConfigChange = (field, value) => {
    setScoreConfig({
      ...scoreConfig,
      [field]: parseFloat(value) || 0
    });
  };

  const saveScoreConfig = async () => {
    try {
      await axios.put(`${API}/score/config`, scoreConfig, {
        headers: getAuthHeader()
      });
      toast.success('Конфигурация сохранена');
      setEditingScore(false);
      // Trigger graph redraw after save
      setTimeout(() => {
        if (canvasRef.current) {
          drawScoreCurve();
        }
      }, 100);
    } catch (error) {
      console.error('Error saving score config:', error);
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const handleTestDistance = () => {
    const dist = parseFloat(testDistance);
    if (isNaN(dist) || dist < 0) {
      toast.error('Введите корректное расстояние');
      return;
    }
    
    const score = calculateScoreForDistance(dist);
    setTestResult({ distance: dist, score: Math.round(score) });
    drawScoreCurve(); // Redraw with test point
  };

  const calculateScoreForDistance = (distance) => {
    if (!scoreConfig) return 0;
    if (distance > scoreConfig.zero_after_meters) return 0;
    
    for (const seg of scoreConfig.intervals) {
      const a = seg.start_m;
      const b = seg.end_m;
      const Sa = seg.start_score;
      const Sb = seg.end_score;
      const k = seg.k;
      
      if (distance <= b) {
        if (distance <= a) return Sa;
        
        const x = (distance - a) / (b - a);
        const ek = Math.exp(-k);
        const w = (Math.exp(-k * x) - ek) / (1 - ek);
        return Sb + (Sa - Sb) * w;
      }
    }
    return scoreConfig.min_points;
  };

  const handleZoomPreset = (range) => {
    setViewRange(range);
  };

  const fetchWinnerConfig = async () => {
    setLoadingWinners(true);
    try {
      const response = await axios.get(`${API}/winner-config`, {
        headers: getAuthHeader()
      });
      setWinnerConfig(response.data);
    } catch (error) {
      console.error('Error fetching winner config:', error);
      toast.error('Ошибка загрузки конфигурации');
    } finally {
      setLoadingWinners(false);
    }
  };

  const saveWinnerConfig = async () => {
    try {
      await axios.put(`${API}/winner-config`, winnerConfig, {
        headers: getAuthHeader()
      });
      toast.success('Глобальная конфигурация сохранена');
      setEditingWinners(false);
    } catch (error) {
      console.error('Error saving winner config:', error);
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const deleteAllResults = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить ВСЕ результаты игр? Это действие необратимо!')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/game-results/all`, {
        headers: getAuthHeader()
      });
      toast.success(`Удалено результатов: ${response.data.deleted_count}`);
    } catch (error) {
      console.error('Error deleting results:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const fetchEmailSettings = async () => {
    setLoadingEmail(true);
    try {
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: getAuthHeader()
      });
      setEmailSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Ошибка загрузки настроек email');
    } finally {
      setLoadingEmail(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      await axios.put(`${API}/admin/email-settings`, emailSettings, {
        headers: getAuthHeader()
      });
      toast.success('Настройки email сохранены');
      setEditingEmail(false);
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      toast.error('Введите корректный email адрес');
      return;
    }

    setSendingTest(true);
    try {
      await axios.post(
        `${API}/admin/test-email?recipient_email=${encodeURIComponent(testEmailAddress)}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Тестовое письмо отправлено на ${testEmailAddress}`);
      setTestEmailAddress('');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Ошибка отправки тестового письма');
    } finally {
      setSendingTest(false);
    }
  };

  const fetchUpdateSettings = async () => {
    setLoadingUpdates(true);
    try {
      // Получаем настройки обновлений
      const response = await axios.get(`${API}/admin/updates/settings`, {
        headers: getAuthHeader()
      });
      
      if (response.data.success) {
        setUpdateSettings(response.data.settings || {
          repository_url: '',
          branch: 'production',
          github_token: '',
          auto_backup: true
        });
      }
    } catch (error) {
      console.error('Error fetching update settings:', error);
      toast.error('Ошибка загрузки настроек обновлений');
      // Устанавливаем значения по умолчанию
      setUpdateSettings({
        repository_url: '',
        branch: 'production',
        github_token: '',
        auto_backup: true
      });
    } finally {
      setLoadingUpdates(false);
    }
  };

  const saveUpdateSettings = async () => {
    try {
      // Валидация
      if (!updateSettings.repository_url) {
        toast.error('URL репозитория обязателен');
        return;
      }
      
      if (!updateSettings.branch) {
        toast.error('Название ветки обязательно');
        return;
      }
      
      const response = await axios.post(`${API}/admin/updates/settings`, updateSettings, {
        headers: getAuthHeader()
      });
      
      if (response.data.success) {
        toast.success('Настройки обновлений сохранены');
        setEditingUpdates(false);
      }
    } catch (error) {
      console.error('Error saving update settings:', error);
      toast.error(error.response?.data?.detail || 'Ошибка сохранения настроек');
    }
  };

  const checkForUpdates = async () => {
    setCheckingUpdates(true);
    setUpdateInfo(null);
    try {
      const response = await axios.get(`${API}/admin/updates/check`, {
        headers: getAuthHeader()
      });
      
      if (response.data.success) {
        setUpdateInfo(response.data);
        
        if (response.data.has_update) {
          toast.success('Доступна новая версия!');
        } else {
          toast.info('У вас установлена последняя версия');
        }
      } else {
        toast.error(response.data.error || 'Не удалось проверить обновления');
      }
    } catch (error) {
      console.error('Error checking updates:', error);
      toast.error(error.response?.data?.detail || 'Ошибка проверки обновлений');
    } finally {
      setCheckingUpdates(false);
    }
  };

  const performUpdate = async () => {
    setUpdatingApp(true);
    setUpdateProgress(0);
    setUpdateStage('Инициализация обновления...');
    
    try {
      // Симуляция этапов обновления для прогресс-бара
      const stages = [
        { progress: 10, stage: 'Создание резервной копии...' },
        { progress: 30, stage: 'Загрузка обновления из GitHub...' },
        { progress: 50, stage: 'Применение обновления...' },
        { progress: 70, stage: 'Установка зависимостей...' },
        { progress: 85, stage: 'Сборка фронтенда...' },
        { progress: 95, stage: 'Финализация...' }
      ];
      
      // Запускаем симуляцию прогресса
      let currentStage = 0;
      const progressInterval = setInterval(() => {
        if (currentStage < stages.length) {
          setUpdateProgress(stages[currentStage].progress);
          setUpdateStage(stages[currentStage].stage);
          currentStage++;
        }
      }, 5000); // Обновляем каждые 5 секунд
      
      const response = await axios.post(`${API}/admin/updates/apply`, {}, {
        headers: getAuthHeader(),
        timeout: 300000 // 5 minutes
      });
      
      clearInterval(progressInterval);
      
      if (response.data.success) {
        setUpdateProgress(100);
        setUpdateStage('Обновление успешно завершено!');
        toast.success('Обновление успешно установлено! Страница перезагрузится через 5 секунд.');
        setUpdateInfo(null);
        
        // Автоматически перезагрузить страницу через 5 секунд
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      } else {
        clearInterval(progressInterval);
        toast.error(response.data.message || 'Обновление не удалось');
        setUpdatingApp(false);
      }
    } catch (error) {
      console.error('Error performing update:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при обновлении');
      setUpdatingApp(false);
    }
  };

  const fetchVersionHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/version-history`, {
        headers: getAuthHeader()
      });
      setVersionHistory(response.data.versions || []);
    } catch (error) {
      console.error('Error fetching version history:', error);
    }
  };

  const rollbackToVersion = async (versionId) => {
    if (!window.confirm('Вы уверены, что хотите откатиться к этой версии? Текущее состояние будет заменено.')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/rollback/${versionId}`, {}, {
        headers: getAuthHeader(),
        timeout: 180000 // 3 minutes
      });
      
      if (response.data.success) {
        toast.success('Откат выполнен успешно! Перезагрузите страницу.');
        fetchVersionHistory();
      } else {
        toast.error('Откат не удался');
      }
    } catch (error) {
      console.error('Error rolling back:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при откате');
    }
  };

  // Backups management functions
  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await axios.get(`${API}/admin/backups/list`, {
        headers: getAuthHeader()
      });
      
      if (response.data.success) {
        setTemporaryBackups(response.data.temporary || []);
        setPermanentBackups(response.data.permanent || []);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Ошибка загрузки резервных копий');
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleDragStart = (e, backup, type) => {
    setDraggedBackup({ ...backup, sourceType: type });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropToPermanent = async (e) => {
    e.preventDefault();
    
    if (!draggedBackup || draggedBackup.sourceType === 'permanent') {
      return;
    }

    try {
      await axios.post(`${API}/admin/backups/move-to-permanent`, {
        path: draggedBackup.path,
        notes: draggedBackup.notes || ''
      }, {
        headers: getAuthHeader()
      });
      
      toast.success('Резервная копия перемещена в постоянное хранилище');
      fetchBackups();
    } catch (error) {
      console.error('Error moving backup:', error);
      toast.error('Ошибка перемещения резервной копии');
    } finally {
      setDraggedBackup(null);
    }
  };

  const handleDropToTemporary = async (e) => {
    e.preventDefault();
    
    if (!draggedBackup || draggedBackup.sourceType === 'temporary') {
      return;
    }

    try {
      await axios.post(`${API}/admin/backups/move-to-temporary`, {
        path: draggedBackup.path
      }, {
        headers: getAuthHeader()
      });
      
      toast.success('Резервная копия перемещена в оперативное хранилище');
      fetchBackups();
    } catch (error) {
      console.error('Error moving backup:', error);
      toast.error('Ошибка перемещения резервной копии');
    } finally {
      setDraggedBackup(null);
    }
  };

  const handleEditNotes = (backup) => {
    setEditingNotes(backup);
    setNotesText(backup.notes || '');
  };

  const handleSaveNotes = async () => {
    try {
      await axios.post(`${API}/admin/backups/update-notes`, {
        path: editingNotes.path,
        notes: notesText
      }, {
        headers: getAuthHeader()
      });
      
      toast.success('Заметки сохранены');
      setEditingNotes(null);
      setNotesText('');
      fetchBackups();
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Ошибка сохранения заметок');
    }
  };

  const handleDeletePermanentBackup = async (backup) => {
    if (!window.confirm(`Вы уверены, что хотите удалить резервную копию "${backup.name}"? Это действие необратимо.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/backups/delete?backup_path=${encodeURIComponent(backup.path)}`, {
        headers: getAuthHeader()
      });
      
      toast.success('Резервная копия удалена');
      fetchBackups();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления резервной копии');
    }
  };

  const handleRollbackFromBackup = async (backup) => {
    if (!window.confirm(`Вы уверены, что хотите откатиться к этой копии?\n\nДата: ${new Date(backup.created_at).toLocaleString('ru-RU')}\n\nТекущее состояние будет заменено.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/backups/rollback?backup_path=${encodeURIComponent(backup.path)}`, {}, {
        headers: getAuthHeader(),
        timeout: 180000 // 3 minutes
      });
      
      if (response.data.success) {
        toast.success('Откат выполнен! Перезагрузите страницу через 5 секунд.');
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      } else {
        toast.error('Откат не удался');
      }
    } catch (error) {
      console.error('Error rolling back from backup:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при откате');
    }
  };

  const drawScoreCurve = () => {
    const canvas = canvasRef.current;
    if (!canvas || !scoreConfig) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const padding = 50;
    const graphWidth = width - 2 * padding;
    const graphHeight = height - 2 * padding;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Use view range for zoom
    const minDistance = viewRange.start;
    const maxDistance = viewRange.end;
    const maxScore = scoreConfig.base_max_points;

    // Draw axes
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, height - padding);
    ctx.lineTo(width - padding, height - padding);
    ctx.stroke();

    // Draw axis labels
    ctx.fillStyle = '#333';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    
    // X-axis labels (distance in meters)
    const xLabels = [
      minDistance,
      minDistance + (maxDistance - minDistance) / 4,
      minDistance + (maxDistance - minDistance) / 2,
      minDistance + (3 * (maxDistance - minDistance)) / 4,
      maxDistance
    ];
    xLabels.forEach((dist, i) => {
      const x = padding + (i / (xLabels.length - 1)) * graphWidth;
      const label = dist >= 1000 ? `${(dist / 1000).toFixed(1)}km` : `${Math.round(dist)}m`;
      ctx.fillText(label, x, height - padding + 20);
    });

    // Y-axis labels (score)
    ctx.textAlign = 'right';
    const yLabels = [0, maxScore / 4, maxScore / 2, (3 * maxScore) / 4, maxScore];
    yLabels.forEach((score, i) => {
      const y = height - padding - (i / (yLabels.length - 1)) * graphHeight;
      ctx.fillText(Math.round(score), padding - 10, y + 4);
    });

    // Axis titles
    ctx.fillStyle = '#666';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Расстояние (метры)', width / 2, height - 10);
    
    ctx.save();
    ctx.translate(15, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Очки', 0, 0);
    ctx.restore();

    // Draw the curve
    ctx.strokeStyle = '#0a4797';
    ctx.lineWidth = 3;
    ctx.beginPath();

    const steps = 500;
    let firstPoint = true;
    for (let i = 0; i <= steps; i++) {
      const distance = minDistance + (i / steps) * (maxDistance - minDistance);
      const score = calculateScoreForDistance(distance);
      
      const x = padding + ((distance - minDistance) / (maxDistance - minDistance)) * graphWidth;
      const y = height - padding - (score / maxScore) * graphHeight;
      
      if (firstPoint) {
        ctx.moveTo(x, y);
        firstPoint = false;
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();

    // Draw interval markers
    ctx.fillStyle = '#ff6b6b';
    scoreConfig.intervals.forEach((interval, idx) => {
      if (interval.start_m >= minDistance && interval.start_m <= maxDistance && idx > 0) {
        const x = padding + ((interval.start_m - minDistance) / (maxDistance - minDistance)) * graphWidth;
        const score = calculateScoreForDistance(interval.start_m);
        ctx.beginPath();
        ctx.arc(x, height - padding - (score / maxScore) * graphHeight, 4, 0, 2 * Math.PI);
        ctx.fill();
      }
    });

    // Draw test point if exists
    if (testResult && testResult.distance >= minDistance && testResult.distance <= maxDistance) {
      const x = padding + ((testResult.distance - minDistance) / (maxDistance - minDistance)) * graphWidth;
      const y = height - padding - (testResult.score / maxScore) * graphHeight;
      
      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, 2 * Math.PI);
      ctx.fill();
      
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  };

  if (loadingUsers && activeTab === 'users') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div>Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Shield size={32} color="#0a4797" />
          <h1>Админ-панель</h1>
        </div>
        <Button variant="outline" onClick={() => navigate('/')}>
          <ArrowLeft size={18} style={{ marginRight: '0.5rem' }} />
          Назад
        </Button>
      </div>

      {/* Tabs — desktop horizontal row + mobile dropdown (≤768px) */}
      {(() => {
        const ADMIN_TABS = [
          { id: 'users',      icon: Users,      label: 'Пользователи' },
          { id: 'score',      icon: TrendingUp, label: 'Формула очков' },
          { id: 'winners',    icon: Shield,     label: 'Победители' },
          { id: 'email',      icon: Mail,       label: 'Настройки Email' },
          { id: 'updates',    icon: RefreshCw,  label: 'Обновления' },
          { id: 'sections',   icon: FileText,   label: 'Разделы' },
          { id: 'objects',    icon: Layers,     label: 'Объекты' },
          { id: 'analytics',  icon: BarChart3,  label: 'Аналитика и мониторинг' },
          { id: 'antifraud',  icon: Shield,     label: 'Антифрод' },
          { id: 'settings',   icon: SettingsIcon, label: 'Настройки' },
        ];
        const current = ADMIN_TABS.find(t => t.id === activeTab) || ADMIN_TABS[0];
        const CurrentIcon = current.icon;
        return (
          <>
            {/* Desktop: icon-only horizontal tabs (text labels appear on hover via title) */}
            <div className="admin-tabs-container admin-tabs-desktop">
              {ADMIN_TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    title={tab.label}
                    onClick={() => setActiveTab(tab.id)}
                    className={`admin-tab-button ${isActive ? 'active' : ''}`}
                    data-testid={`admin-tab-${tab.id}`}
                  >
                    <Icon size={22} />
                  </button>
                );
              })}
            </div>

            {/* Mobile: shadcn DropdownMenu, grouped by Content / Analytics / Settings.
                The whole pill is the trigger button — tapping anywhere opens the menu. */}
            {(() => {
              const GROUPS = [
                { label: 'Контент',   ids: ['users', 'winners', 'sections', 'objects'] },
                { label: 'Аналитика', ids: ['analytics', 'antifraud'] },
                { label: 'Настройки', ids: ['score', 'email', 'updates', 'settings'] },
              ];
              const byId = Object.fromEntries(ADMIN_TABS.map(t => [t.id, t]));
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="admin-tabs-mobile"
                      data-testid="admin-tabs-mobile"
                      aria-label="Раздел админ-панели"
                    >
                      <span className="admin-tabs-mobile-icon">
                        <CurrentIcon size={18} />
                      </span>
                      <span className="admin-tabs-mobile-label">{current.label}</span>
                      <ChevronDown size={18} className="admin-tabs-mobile-caret" aria-hidden="true" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sideOffset={8}
                    className="admin-tabs-menu"
                    data-testid="admin-tab-menu"
                  >
                    {GROUPS.map((g, gi) => (
                      <React.Fragment key={g.label}>
                        {gi > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuLabel className="admin-tabs-menu-group">
                          {g.label}
                        </DropdownMenuLabel>
                        {g.ids.map(id => {
                          const tab = byId[id];
                          if (!tab) return null;
                          const Icon = tab.icon;
                          const isActive = activeTab === id;
                          return (
                            <DropdownMenuItem
                              key={id}
                              onSelect={() => setActiveTab(id)}
                              className={'admin-tabs-menu-item' + (isActive ? ' is-active' : '')}
                              data-testid={`admin-tab-menu-${id}`}
                            >
                              <Icon size={18} className="admin-tabs-menu-item-icon" />
                              <span className="admin-tabs-menu-item-label">{tab.label}</span>
                              {isActive && <Check size={16} className="admin-tabs-menu-item-check" />}
                            </DropdownMenuItem>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}
          </>
        );
      })()}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <>
          {/* Virality / referral config (collapsible) */}
          <details
            data-testid="admin-virality-collapsible"
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              marginBottom: '1rem',
              background: '#fafbff',
              overflow: 'hidden',
            }}
          >
            <summary
              style={{
                listStyle: 'none',
                cursor: 'pointer',
                padding: '0.75rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontWeight: 600,
                color: '#0a4797',
                userSelect: 'none',
              }}
            >
              <Share2 size={18} />
              Настройки вирусности (баллы и стоимость подсказок)
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b', fontWeight: 400 }}>
                нажмите, чтобы развернуть
              </span>
            </summary>
            <div style={{ padding: '0.5rem 1rem 1rem' }}>
              <AdminViralityConfig />
            </div>
          </details>

          {/* Sorting and Pagination Controls */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '0.75rem',
            marginBottom: '1rem',
            padding: '0.5rem 0'
          }}>
            {/* Search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Поиск:</span>
              <input
                type="text"
                value={userSearchQuery}
                onChange={(e) => { setUserSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="Почта или имя…"
                data-testid="admin-users-search"
                style={{
                  flex: '1 1 200px',
                  maxWidth: 360,
                  padding: '0.4rem 0.6rem',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: '0.9rem',
                  outline: 'none',
                }}
              />
              {userSearchQuery && (
                <button
                  type="button"
                  onClick={() => { setUserSearchQuery(''); setCurrentPage(1); }}
                  data-testid="admin-users-search-clear"
                  style={{
                    border: '1px solid #d1d5db', background: '#fff', borderRadius: 6,
                    padding: '0.3rem 0.6rem', cursor: 'pointer', color: '#6b7280', fontSize: '0.85rem'
                  }}
                >Очистить</button>
              )}
              <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                Найдено: {filteredUsers.length}
              </span>
            </div>
            {/* Sorting */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Сортировка:</span>
              <button
                onClick={() => {
                  if (sortBy === 'total_points') {
                    setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
                  } else {
                    setSortBy('total_points');
                    setSortOrder('desc');
                  }
                  setCurrentPage(1);
                }}
                style={{
                  padding: '0.4rem 0.75rem',
                  border: sortBy === 'total_points' ? '2px solid #f59e0b' : '1px solid #ddd',
                  borderRadius: '6px',
                  background: sortBy === 'total_points' ? '#fffbeb' : 'white',
                  cursor: 'pointer',
                  fontWeight: sortBy === 'total_points' ? '600' : '400',
                  color: sortBy === 'total_points' ? '#b45309' : '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                ⭐ Баллы {sortBy === 'total_points' && (sortOrder === 'desc' ? '↓' : '↑')}
              </button>
              <button
                onClick={() => {
                  if (sortBy === 'created_at') {
                    setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
                  } else {
                    setSortBy('created_at');
                    setSortOrder('desc');
                  }
                  setCurrentPage(1);
                }}
                style={{
                  padding: '0.4rem 0.75rem',
                  border: sortBy === 'created_at' ? '2px solid #667eea' : '1px solid #ddd',
                  borderRadius: '6px',
                  background: sortBy === 'created_at' ? '#eef2ff' : 'white',
                  cursor: 'pointer',
                  fontWeight: sortBy === 'created_at' ? '600' : '400',
                  color: sortBy === 'created_at' ? '#4338ca' : '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                📅 Дата регистрации {sortBy === 'created_at' && (sortOrder === 'desc' ? '↓' : '↑')}
              </button>
            </div>

            {/* User count info */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>
                Всего пользователей: {users.length}
              </span>
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Полное имя</th>
                  <th style={{ textAlign: 'center' }}>Страна</th>
                  <th>Роль</th>
                  <th>Баллы</th>
                  <th>Статус</th>
                  <th>Дата регистрации</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {[...filteredUsers]
                  .sort((a, b) => {
                    if (sortBy === 'total_points') {
                      return sortOrder === 'desc' 
                        ? (b.total_points || 0) - (a.total_points || 0)
                        : (a.total_points || 0) - (b.total_points || 0);
                    } else {
                      const dateA = new Date(a.created_at);
                      const dateB = new Date(b.created_at);
                      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
                    }
                  })
                  .slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage)
                  .map((u) => (
                  <tr key={u.id}>
                    <td>
                      <a 
                        href={`/profile/${u.id}`} 
                        style={{ color: '#0a4797', textDecoration: 'none' }}
                        onClick={(e) => { e.preventDefault(); navigate(`/profile/${u.id}`); }}
                      >
                        {u.email}
                      </a>
                    </td>
                    <td>
                      <a 
                        href={`/profile/${u.id}`} 
                        style={{ color: '#0a4797', textDecoration: 'none' }}
                        onClick={(e) => { e.preventDefault(); navigate(`/profile/${u.id}`); }}
                      >
                        {u.full_name || '-'}
                      </a>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {u.country ? (
                        <span
                          className={`fi fi-${String(u.country).toLowerCase()}`}
                          title={u.country}
                          aria-label={`Country: ${u.country}`}
                          data-testid={`admin-user-flag-${u.id}`}
                          style={{
                            display: 'inline-block',
                            width: 24,
                            height: 18,
                            borderRadius: 2,
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                            verticalAlign: 'middle',
                          }}
                        />
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={`user-role-badge ${u.role}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        fontWeight: '600',
                        color: (u.total_points || 0) > 0 ? '#f59e0b' : '#999'
                      }}>
                        {Math.round(u.total_points || 0)} ⭐
                      </span>
                    </td>
                    <td>
                      {u.blocked ? (
                        <span
                          title="Заблокирован"
                          aria-label="Заблокирован"
                          data-testid={`user-status-icon-blocked-${u.id}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 30, height: 30, borderRadius: '50%',
                            background: '#fee2e2', color: '#b91c1c',
                            border: '1px solid #fecaca',
                          }}
                        >
                          <Lock size={16} />
                        </span>
                      ) : !u.email_verified ? (
                        <span
                          title="Не подтверждён"
                          aria-label="Не подтверждён"
                          data-testid={`user-status-icon-pending-${u.id}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 30, height: 30, borderRadius: '50%',
                            background: '#fef3c7', color: '#92400e',
                            border: '1px solid #fde68a',
                          }}
                        >
                          <AlertCircle size={16} />
                        </span>
                      ) : (
                        <span
                          title="Активен"
                          aria-label="Активен"
                          data-testid={`user-status-icon-active-${u.id}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 30, height: 30, borderRadius: '50%',
                            background: '#dcfce7', color: '#15803d',
                            border: '1px solid #bbf7d0',
                          }}
                        >
                          <CheckCircle size={16} />
                        </span>
                      )}
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString('ru-RU')}</td>
                    <td>
                      <div className="user-actions">
                        <button
                          className="btn-edit"
                          onClick={() => handleRoleChange(u.id, u.role)}
                          disabled={actionLoading === u.id || u.id === user.id}
                          title="Изменить роль"
                        >
                          <Edit size={14} />
                        </button>
                        
                        {u.blocked ? (
                          <button
                            className="btn-unblock"
                            onClick={() => handleBlockToggle(u.id, u.blocked)}
                            disabled={actionLoading === u.id || u.id === user.id}
                            title="Разблокировать"
                          >
                            <Unlock size={14} />
                          </button>
                        ) : (
                          <button
                            className="btn-block"
                            onClick={() => handleBlockToggle(u.id, u.blocked)}
                            disabled={actionLoading === u.id || u.id === user.id}
                            title="Заблокировать"
                          >
                            <Lock size={14} />
                          </button>
                        )}
                        
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(u.id, u.email)}
                          disabled={actionLoading === u.id || u.id === user.id}
                          title="Удалить"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div style={{ 
                padding: '3rem', 
                textAlign: 'center', 
                color: '#718096' 
              }}>
                {userSearchQuery ? 'Ничего не найдено' : 'Нет пользователей'}
              </div>
            )}
          </div>

          {/* Mobile Cards View */}
          <div className="users-cards-container">
            {[...filteredUsers]
              .sort((a, b) => {
                if (sortBy === 'total_points') {
                  return sortOrder === 'desc' 
                    ? (b.total_points || 0) - (a.total_points || 0)
                    : (a.total_points || 0) - (b.total_points || 0);
                } else {
                  const dateA = new Date(a.created_at);
                  const dateB = new Date(b.created_at);
                  return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
                }
              })
              .slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage)
              .map((u) => (
              <div key={u.id} className="user-card">
                <div className="user-card-header">
                  <div className="user-card-info">
                    <a 
                      href={`/profile/${u.id}`} 
                      className="user-card-email"
                      style={{ color: '#0a4797', textDecoration: 'none' }}
                      onClick={(e) => { e.preventDefault(); navigate(`/profile/${u.id}`); }}
                    >
                      {u.email}
                    </a>
                    <a 
                      href={`/profile/${u.id}`} 
                      className="user-card-name"
                      style={{ color: '#0a4797', textDecoration: 'none' }}
                      onClick={(e) => { e.preventDefault(); navigate(`/profile/${u.id}`); }}
                    >
                      {u.full_name || 'Без имени'}
                    </a>
                  </div>
                </div>

                <div className="user-card-badges">
                  {u.country && (
                    <span
                      className={`fi fi-${String(u.country).toLowerCase()}`}
                      title={u.country}
                      aria-label={`Country: ${u.country}`}
                      data-testid={`admin-user-flag-mobile-${u.id}`}
                      style={{
                        display: 'inline-block',
                        width: 22,
                        height: 16,
                        borderRadius: 2,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                        verticalAlign: 'middle',
                      }}
                    />
                  )}
                  <span className={`user-role-badge ${u.role}`}>
                    {u.role}
                  </span>
                  <span style={{
                    fontWeight: '600',
                    color: (u.total_points || 0) > 0 ? '#f59e0b' : '#999',
                    padding: '4px 8px',
                    background: '#fffbeb',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    {Math.round(u.total_points || 0)} ⭐
                  </span>
                  {u.blocked ? (
                    <span
                      title="Заблокирован"
                      aria-label="Заблокирован"
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: '50%',
                        background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca',
                      }}
                    >
                      <Lock size={14} />
                    </span>
                  ) : !u.email_verified ? (
                    <span
                      title="Не подтверждён"
                      aria-label="Не подтверждён"
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: '50%',
                        background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                      }}
                    >
                      <AlertCircle size={14} />
                    </span>
                  ) : (
                    <span
                      title="Активен"
                      aria-label="Активен"
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: '50%',
                        background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0',
                      }}
                    >
                      <CheckCircle size={14} />
                    </span>
                  )}
                </div>

                <div className="user-card-meta">
                  <span>📅 {new Date(u.created_at).toLocaleDateString('ru-RU')}</span>
                </div>

                <div className="user-card-actions">
                  <button
                    className="btn-edit"
                    onClick={() => handleRoleChange(u.id, u.role)}
                    disabled={actionLoading === u.id || u.id === user.id}
                    title="Изменить роль"
                  >
                    <Edit size={16} />
                    <span>Роль</span>
                  </button>
                  
                  {u.blocked ? (
                    <button
                      className="btn-unblock"
                      onClick={() => handleBlockToggle(u.id, u.blocked)}
                      disabled={actionLoading === u.id || u.id === user.id}
                      title="Разблокировать"
                    >
                      <Unlock size={16} />
                      <span>Разблокировать</span>
                    </button>
                  ) : (
                    <button
                      className="btn-block"
                      onClick={() => handleBlockToggle(u.id, u.blocked)}
                      disabled={actionLoading === u.id || u.id === user.id}
                      title="Заблокировать"
                    >
                      <Lock size={16} />
                      <span>Заблокировать</span>
                    </button>
                  )}
                  
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(u.id, u.email)}
                    disabled={actionLoading === u.id || u.id === user.id}
                    title="Удалить"
                  >
                    <Trash2 size={16} />
                    <span>Удалить</span>
                  </button>
                </div>
              </div>
            ))}

            {filteredUsers.length === 0 && (
              <div style={{ 
                padding: '3rem', 
                textAlign: 'center', 
                color: '#718096' 
              }}>
                {userSearchQuery ? 'Ничего не найдено' : 'Нет пользователей'}
              </div>
            )}
          </div>

          {/* Pagination - at bottom */}
          {filteredUsers.length > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '1rem 0',
              marginTop: '1rem'
            }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: currentPage === 1 ? '#f5f5f5' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                ← Назад
              </button>
              <span style={{ padding: '0 1rem', color: '#333' }}>
                Стр. {currentPage} из {Math.ceil(filteredUsers.length / usersPerPage) || 1}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredUsers.length / usersPerPage), p + 1))}
                disabled={currentPage >= Math.ceil(filteredUsers.length / usersPerPage)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: currentPage >= Math.ceil(filteredUsers.length / usersPerPage) ? '#f5f5f5' : 'white',
                  cursor: currentPage >= Math.ceil(filteredUsers.length / usersPerPage) ? 'not-allowed' : 'pointer',
                  opacity: currentPage >= Math.ceil(filteredUsers.length / usersPerPage) ? 0.5 : 1
                }}
              >
                Вперёд →
              </button>
            </div>
          )}
        </>
      )}

      {/* Score Tab Content */}
      {activeTab === 'score' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {loadingScore ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>Загрузка...</div>
          ) : scoreConfig ? (
            <>
              {/* Score Curve Visualization */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ marginBottom: '1rem', color: '#2d3748' }}>График кривой начисления очков</h2>
                
                {/* Zoom controls */}
                <div style={{ 
                  display: 'flex', 
                  gap: '0.5rem', 
                  marginBottom: '1rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => handleZoomPreset({ start: 0, end: 500 })}
                    style={{
                      padding: '0.5rem 1rem',
                      background: viewRange.start === 0 && viewRange.end === 500 ? '#0a4797' : '#e2e8f0',
                      color: viewRange.start === 0 && viewRange.end === 500 ? 'white' : '#4a5568',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    0-500м
                  </button>
                  <button
                    onClick={() => handleZoomPreset({ start: 0, end: 3000 })}
                    style={{
                      padding: '0.5rem 1rem',
                      background: viewRange.start === 0 && viewRange.end === 3000 ? '#0a4797' : '#e2e8f0',
                      color: viewRange.start === 0 && viewRange.end === 3000 ? 'white' : '#4a5568',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    0-3км
                  </button>
                  <button
                    onClick={() => handleZoomPreset({ start: 0, end: 50000 })}
                    style={{
                      padding: '0.5rem 1rem',
                      background: viewRange.start === 0 && viewRange.end === 50000 ? '#0a4797' : '#e2e8f0',
                      color: viewRange.start === 0 && viewRange.end === 50000 ? 'white' : '#4a5568',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    0-50км
                  </button>
                  <button
                    onClick={() => handleZoomPreset({ start: 0, end: 1000000 })}
                    style={{
                      padding: '0.5rem 1rem',
                      background: viewRange.start === 0 && viewRange.end === 1000000 ? '#0a4797' : '#e2e8f0',
                      color: viewRange.start === 0 && viewRange.end === 1000000 ? 'white' : '#4a5568',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    0-1000км
                  </button>
                  <button
                    onClick={() => handleZoomPreset({ start: 0, end: scoreConfig.zero_after_meters })}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#e2e8f0',
                      color: '#4a5568',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Полный диапазон
                  </button>
                  <span style={{ 
                    padding: '0.5rem 1rem',
                    color: '#718096',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    Используйте колесо мыши для зума
                  </span>
                </div>

                <canvas
                  ref={canvasRef}
                  width={800}
                  height={400}
                  style={{ 
                    maxWidth: '100%', 
                    height: 'auto', 
                    border: '1px solid #e2e8f0',
                    cursor: 'zoom-in'
                  }}
                />

                {/* Test distance input */}
                <div style={{ 
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: '#f7fafc',
                  borderRadius: '6px'
                }}>
                  <h3 style={{ marginBottom: '1rem', color: '#4a5568', fontSize: '16px' }}>
                    Тестирование формулы
                  </h3>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontSize: '14px', 
                        color: '#4a5568' 
                      }}>
                        Расстояние (метры)
                      </label>
                      <input
                        type="number"
                        value={testDistance}
                        onChange={(e) => setTestDistance(e.target.value)}
                        placeholder="Например: 1000"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <Button 
                      onClick={handleTestDistance}
                      style={{ 
                        background: '#0a4797',
                        padding: '0.5rem 1.5rem'
                      }}
                    >
                      Рассчитать
                    </Button>
                    {testResult && (
                      <div style={{
                        padding: '0.5rem 1rem',
                        background: '#22c55e',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {testResult.distance}м → {testResult.score} очков
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Score Configuration Form */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ color: '#2d3748' }}>Конфигурация формулы</h2>
                  {editingScore ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button onClick={saveScoreConfig} style={{ background: '#0a4797' }}>
                        Сохранить
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setEditingScore(false);
                        fetchScoreConfig();
                      }}>
                        Отмена
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={() => setEditingScore(true)}>
                      Редактировать
                    </Button>
                  )}
                </div>

                {/* Global Parameters */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem', color: '#4a5568' }}>Общие параметры</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568' }}>
                        Максимальные очки
                      </label>
                      <input
                        type="number"
                        value={scoreConfig.base_max_points}
                        onChange={(e) => handleConfigChange('base_max_points', e.target.value)}
                        disabled={!editingScore}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px',
                          background: editingScore ? 'white' : '#f7fafc'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568' }}>
                        Минимальные очки
                      </label>
                      <input
                        type="number"
                        value={scoreConfig.min_points}
                        onChange={(e) => handleConfigChange('min_points', e.target.value)}
                        disabled={!editingScore}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px',
                          background: editingScore ? 'white' : '#f7fafc'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568' }}>
                        Ноль очков после (м)
                      </label>
                      <input
                        type="number"
                        value={scoreConfig.zero_after_meters}
                        onChange={(e) => handleConfigChange('zero_after_meters', e.target.value)}
                        disabled={!editingScore}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px',
                          background: editingScore ? 'white' : '#f7fafc'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Intervals Table */}
                <div>
                  <h3 style={{ marginBottom: '1rem', color: '#4a5568' }}>Интервалы</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4a5568', fontSize: '14px' }}>Начало (м)</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4a5568', fontSize: '14px' }}>Конец (м)</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4a5568', fontSize: '14px' }}>Начальные очки</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4a5568', fontSize: '14px' }}>Конечные очки</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4a5568', fontSize: '14px' }}>Коэффициент k</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scoreConfig.intervals.map((interval, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '0.75rem' }}>
                            <input
                              type="number"
                              value={interval.start_m}
                              onChange={(e) => handleIntervalChange(idx, 'start_m', e.target.value)}
                              disabled={!editingScore}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #cbd5e0',
                                borderRadius: '4px',
                                background: editingScore ? 'white' : '#f7fafc'
                              }}
                            />
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <input
                              type="number"
                              value={interval.end_m}
                              onChange={(e) => handleIntervalChange(idx, 'end_m', e.target.value)}
                              disabled={!editingScore}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #cbd5e0',
                                borderRadius: '4px',
                                background: editingScore ? 'white' : '#f7fafc'
                              }}
                            />
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <input
                              type="number"
                              value={interval.start_score}
                              onChange={(e) => handleIntervalChange(idx, 'start_score', e.target.value)}
                              disabled={!editingScore}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #cbd5e0',
                                borderRadius: '4px',
                                background: editingScore ? 'white' : '#f7fafc'
                              }}
                            />
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <input
                              type="number"
                              value={interval.end_score}
                              onChange={(e) => handleIntervalChange(idx, 'end_score', e.target.value)}
                              disabled={!editingScore}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #cbd5e0',
                                borderRadius: '4px',
                                background: editingScore ? 'white' : '#f7fafc'
                              }}
                            />
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <input
                              type="number"
                              step="0.1"
                              value={interval.k}
                              onChange={(e) => handleIntervalChange(idx, 'k', e.target.value)}
                              disabled={!editingScore}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #cbd5e0',
                                borderRadius: '4px',
                                background: editingScore ? 'white' : '#f7fafc'
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>
              Не удалось загрузить конфигурацию
            </div>
          )}
        </div>
      )}

      {/* Winners Tab Content */}
      {activeTab === 'winners' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {loadingWinners ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>Загрузка...</div>
          ) : winnerConfig ? (
            <>
              {/* Winner Configuration */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ color: '#2d3748' }}>Глобальные пороги для определения победителей</h2>
                  {editingWinners ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button onClick={saveWinnerConfig} style={{ background: '#0a4797' }}>
                        Сохранить
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setEditingWinners(false);
                        fetchWinnerConfig();
                      }}>
                        Отмена
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={() => setEditingWinners(true)}>
                      Редактировать
                    </Button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      1-е место (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={winnerConfig.first_place_threshold}
                      onChange={(e) => setWinnerConfig({
                        ...winnerConfig,
                        first_place_threshold: parseFloat(e.target.value) || 0
                      })}
                      disabled={!editingWinners}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '16px',
                        background: editingWinners ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      2-е место (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={winnerConfig.second_place_threshold}
                      onChange={(e) => setWinnerConfig({
                        ...winnerConfig,
                        second_place_threshold: parseFloat(e.target.value) || 0
                      })}
                      disabled={!editingWinners}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '16px',
                        background: editingWinners ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      3-е место (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={winnerConfig.third_place_threshold}
                      onChange={(e) => setWinnerConfig({
                        ...winnerConfig,
                        third_place_threshold: parseFloat(e.target.value) || 0
                      })}
                      disabled={!editingWinners}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '16px',
                        background: editingWinners ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '1rem', padding: '1rem', background: '#f7fafc', borderRadius: '4px', fontSize: '14px', color: '#4a5568' }}>
                  <strong>Как работает система:</strong>
                  <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                    <li>Пороги применяются ко <strong>всем играм</strong> глобально</li>
                    <li>Игрок должен ответить на <strong>все панорамы</strong> в игре</li>
                    <li>1-е место присваивается первому, кто достиг порога 1-го места</li>
                    <li>2-е и 3-е места присваиваются следующим по убыванию процента</li>
                    <li>Если порог не преодолен, место остается вакантным</li>
                  </ul>
                </div>
              </div>

              {/* Danger Zone */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '2px solid #ef4444'
              }}>
                <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>Опасная зона</h2>
                <p style={{ marginBottom: '1rem', color: '#4a5568' }}>
                  Эти действия необратимы. Используйте только для тестирования.
                </p>
                <Button 
                  onClick={deleteAllResults}
                  style={{ 
                    background: '#ef4444',
                    color: 'white'
                  }}
                >
                  🗑️ Удалить все результаты игр
                </Button>
              </div>
            </>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>
              Не удалось загрузить конфигурацию
            </div>
          )}
        </div>
      )}

      {/* Email Settings Tab Content */}
      {activeTab === 'email' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {/* Referral email template (collapsible, multilingual) */}
          <details
            data-testid="admin-ref-email-tpl-collapsible"
            open
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              background: '#fafbff',
              overflow: 'hidden',
            }}
          >
            <summary
              style={{
                listStyle: 'none', cursor: 'pointer',
                padding: '0.75rem 1rem', display: 'flex',
                alignItems: 'center', gap: 10,
                fontWeight: 600, color: '#0a4797', userSelect: 'none',
              }}
            >
              <Mail size={18} />
              Шаблон письма-приглашения (рефералы) — многоязычный
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b', fontWeight: 400 }}>
                нажмите, чтобы свернуть
              </span>
            </summary>
            <div style={{ padding: '0.5rem 1rem 1rem' }}>
              <AdminReferralEmailTemplate />
            </div>
          </details>

          {loadingEmail ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>Загрузка...</div>
          ) : emailSettings ? (
            <>
              {/* SMTP Settings */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ color: '#2d3748' }}>Настройки SMTP сервера</h2>
                  {editingEmail ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button onClick={saveEmailSettings} style={{ background: '#0a4797' }}>
                        Сохранить
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setEditingEmail(false);
                        fetchEmailSettings();
                      }}>
                        Отмена
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={() => setEditingEmail(true)}>
                      Редактировать
                    </Button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      SMTP Сервер
                    </label>
                    <input
                      type="text"
                      value={emailSettings.smtp_host}
                      onChange={(e) => setEmailSettings({
                        ...emailSettings,
                        smtp_host: e.target.value
                      })}
                      disabled={!editingEmail}
                      placeholder="mail.example.com"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingEmail ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      Порт
                    </label>
                    <input
                      type="number"
                      value={emailSettings.smtp_port}
                      onChange={(e) => setEmailSettings({
                        ...emailSettings,
                        smtp_port: parseInt(e.target.value) || 465
                      })}
                      disabled={!editingEmail}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingEmail ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      Логин (Username)
                    </label>
                    <input
                      type="text"
                      value={emailSettings.smtp_username}
                      onChange={(e) => setEmailSettings({
                        ...emailSettings,
                        smtp_username: e.target.value
                      })}
                      disabled={!editingEmail}
                      placeholder="user@example.com"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingEmail ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      Пароль
                    </label>
                    <input
                      type="password"
                      value={emailSettings.smtp_password}
                      onChange={(e) => setEmailSettings({
                        ...emailSettings,
                        smtp_password: e.target.value
                      })}
                      disabled={!editingEmail}
                      placeholder="••••••••"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingEmail ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      Email отправителя
                    </label>
                    <input
                      type="email"
                      value={emailSettings.sender_email}
                      onChange={(e) => setEmailSettings({
                        ...emailSettings,
                        sender_email: e.target.value
                      })}
                      disabled={!editingEmail}
                      placeholder="noreply@example.com"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingEmail ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      Имя отправителя
                    </label>
                    <input
                      type="text"
                      value={emailSettings.sender_name}
                      onChange={(e) => setEmailSettings({
                        ...emailSettings,
                        sender_name: e.target.value
                      })}
                      disabled={!editingEmail}
                      placeholder="Panoguesser"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingEmail ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Email Template Editor */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ color: '#2d3748', marginBottom: '1rem' }}>Шаблон письма для подтверждения</h2>
                <p style={{ fontSize: '14px', color: '#718096', marginBottom: '1rem' }}>
                  Доступные переменные: <code style={{ background: '#f7fafc', padding: '2px 6px', borderRadius: '4px' }}>{'{'} username {'}'}</code>, <code style={{ background: '#f7fafc', padding: '2px 6px', borderRadius: '4px' }}>{'{'} verification_url {'}'}</code>
                </p>
                <textarea
                  value={emailSettings.email_template || ''}
                  onChange={(e) => setEmailSettings({
                    ...emailSettings,
                    email_template: e.target.value
                  })}
                  disabled={!editingEmail}
                  placeholder="HTML шаблон письма..."
                  style={{
                    width: '100%',
                    minHeight: '300px',
                    padding: '1rem',
                    border: '1px solid #cbd5e0',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    background: editingEmail ? 'white' : '#f7fafc',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Test Email */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ color: '#2d3748', marginBottom: '1rem' }}>Отправить тестовое письмо</h2>
                <p style={{ fontSize: '14px', color: '#718096', marginBottom: '1rem' }}>
                  Отправьте тестовое письмо, чтобы проверить настройки SMTP
                </p>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontSize: '14px', 
                      color: '#4a5568' 
                    }}>
                      Email получателя
                    </label>
                    <input
                      type="email"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      placeholder="test@example.com"
                      disabled={sendingTest}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <Button 
                    onClick={handleSendTestEmail}
                    disabled={sendingTest || !testEmailAddress}
                    style={{ 
                      background: '#22c55e',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Send size={16} />
                    {sendingTest ? 'Отправка...' : 'Отправить'}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>
              Не удалось загрузить настройки email
            </div>
          )}
        </div>
      )}

      {/* Updates Tab Content */}
      {activeTab === 'updates' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {loadingUpdates ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>Загрузка...</div>
          ) : updateSettings ? (
            <>
              {/* Update Settings */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ color: '#2d3748' }}>Настройки обновлений</h2>
                  {editingUpdates ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button onClick={saveUpdateSettings} style={{ background: '#0a4797' }}>
                        Сохранить
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setEditingUpdates(false);
                        fetchUpdateSettings();
                      }}>
                        Отмена
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={() => setEditingUpdates(true)}>
                      Редактировать
                    </Button>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      URL репозитория GitHub *
                    </label>
                    <input
                      type="text"
                      value={updateSettings.repository_url || ''}
                      onChange={(e) => setUpdateSettings({
                        ...updateSettings,
                        repository_url: e.target.value
                      })}
                      disabled={!editingUpdates}
                      placeholder="https://github.com/username/panoguesser"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingUpdates ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                        Ветка *
                      </label>
                      <input
                        type="text"
                        value={updateSettings.branch || 'production'}
                        onChange={(e) => setUpdateSettings({
                          ...updateSettings,
                          branch: e.target.value
                        })}
                        disabled={!editingUpdates}
                        placeholder="production"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingUpdates ? 'white' : '#f7fafc'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#4a5568', fontWeight: 'bold' }}>
                      Personal Access Token (для приватных репозиториев)
                    </label>
                    <input
                      type="password"
                      value={updateSettings.github_token || ''}
                      onChange={(e) => setUpdateSettings({
                        ...updateSettings,
                        github_token: e.target.value
                      })}
                      disabled={!editingUpdates}
                      placeholder="ghp_xxxxxxxxxxxxxxxxxxxx (опционально)"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        fontSize: '14px',
                        background: editingUpdates ? 'white' : '#f7fafc'
                      }}
                    />
                    <small style={{ color: '#718096', fontSize: '12px', marginTop: '0.25rem', display: 'block' }}>
                      Оставьте пустым для публичных репозиториев
                    </small>
                  </div>
                </div>
              </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '14px', color: '#4a5568' }}>
                    <input
                      type="checkbox"
                      checked={updateSettings.auto_backup !== false}
                      onChange={(e) => setUpdateSettings({
                        ...updateSettings,
                        auto_backup: e.target.checked
                      })}
                      disabled={!editingUpdates}
                    />
                    Автоматическое создание резервной копии перед обновлением
                  </label>
                </div>
                
                <div style={{ 
                  padding: '1rem', 
                  background: '#edf2f7', 
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#4a5568'
                }}>
                  <strong>Как получить токен:</strong>
                  <ol style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                    <li>Откройте <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" style={{ color: '#0a4797' }}>GitHub Settings → Tokens</a></li>
                    <li>Нажмите "Generate new token (classic)"</li>
                    <li>Выберите права: <strong>repo</strong></li>
                    <li>Скопируйте токен (начинается с ghp_)</li>
                  </ol>
                </div>
              </div>

              {/* Check for Updates */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ color: '#2d3748', marginBottom: '1rem' }}>Проверка обновлений</h2>
                
                {/* Предупреждение если настройки не заданы */}
                {(!updateSettings.repository_url || !updateSettings.branch) && (
                  <div style={{
                    padding: '1rem',
                    background: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    fontSize: '14px',
                    color: '#78350f'
                  }}>
                    <strong>⚠️ Настройки не заданы</strong>
                    <p style={{ margin: '0.5rem 0 0 0' }}>
                      Пожалуйста, настройте URL репозитория и ветку выше, затем нажмите "Сохранить"
                    </p>
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
                  <Button 
                    onClick={checkForUpdates}
                    disabled={checkingUpdates || !updateSettings.repository_url || !updateSettings.branch}
                    style={{ 
                      background: updateSettings.repository_url && updateSettings.branch ? '#22c55e' : '#cbd5e0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: (!updateSettings.repository_url || !updateSettings.branch) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <RefreshCw size={16} className={checkingUpdates ? 'animate-spin' : ''} />
                    {checkingUpdates ? 'Проверка...' : 'Проверить обновления'}
                  </Button>
                  
                  {updateSettings.repository_url && (
                    <div style={{ fontSize: '13px', color: '#718096' }}>
                      Репозиторий: {updateSettings.repository_url.split('/').slice(-2).join('/')} (ветка: {updateSettings.branch || 'production'})
                    </div>
                  )}
                </div>

                {updateInfo && (
                  <div style={{
                    padding: '1rem',
                    background: updateInfo.has_update ? '#dcfce7' : '#f0f9ff',
                    border: `1px solid ${updateInfo.has_update ? '#22c55e' : '#3b82f6'}`,
                    borderRadius: '6px',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      {updateInfo.has_update ? (
                        <CheckCircle size={20} color="#22c55e" />
                      ) : (
                        <AlertCircle size={20} color="#3b82f6" />
                      )}
                      <strong>
                        {updateInfo.has_update ? 'Доступно обновление!' : 'Обновлений нет'}
                      </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '14px', color: '#4a5568' }}>
                      Текущая версия: {updateInfo.current_version?.version_number || updateInfo.current_version?.git_commit || 'N/A'}
                    </p>
                    {updateInfo.has_update && updateInfo.update_info && (
                      <>
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '14px', color: '#4a5568' }}>
                          Новый коммит: {updateInfo.update_info.latest_commit}
                        </p>
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                          {updateInfo.update_info.commit_message}
                        </p>
                        <div style={{ marginTop: '1rem' }}>
                          <Button 
                            onClick={performUpdate}
                            disabled={updatingApp}
                            style={{ 
                              background: '#f59e0b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <Download size={16} />
                            {updatingApp ? 'Обновление...' : 'Установить обновление'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Backups Management */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                marginTop: '2rem'
              }}>
                <h2 style={{ color: '#2d3748', marginBottom: '1.5rem' }}>Управление резервными копиями</h2>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                  {/* Temporary Backups */}
                  <div
                    onDragOver={handleDragOver}
                    onDrop={handleDropToTemporary}
                    style={{
                      border: '2px dashed #cbd5e0',
                      borderRadius: '8px',
                      padding: '1rem',
                      minHeight: '300px',
                      background: draggedBackup?.sourceType === 'permanent' ? '#f0f9ff' : 'white',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      <RefreshCw size={20} color="#f59e0b" />
                      <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>
                        Оперативные копии (макс. 10)
                      </h3>
                    </div>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '1rem' }}>
                      Автоматически удаляются при превышении лимита. Перетащите копию в постоянное хранилище для сохранения.
                    </p>
                    
                    {loadingBackups ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                        Загрузка...
                      </div>
                    ) : temporaryBackups.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {temporaryBackups.map((backup) => (
                          <div
                            key={backup.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, backup, 'temporary')}
                            style={{
                              padding: '0.75rem',
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              cursor: 'move',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{ fontSize: '13px', color: '#4b5563' }}>
                              {new Date(backup.created_at).toLocaleString('ru-RU')}
                            </div>
                            <Button
                              onClick={() => handleRollbackFromBackup(backup)}
                              variant="outline"
                              style={{
                                fontSize: '11px',
                                padding: '0.25rem 0.75rem',
                                height: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <RotateCcw size={12} />
                              Откатиться
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontStyle: 'italic' }}>
                        Нет оперативных копий
                      </div>
                    )}
                  </div>

                  {/* Permanent Backups */}
                  <div
                    onDragOver={handleDragOver}
                    onDrop={handleDropToPermanent}
                    style={{
                      border: '2px dashed #10b981',
                      borderRadius: '8px',
                      padding: '1rem',
                      minHeight: '300px',
                      background: draggedBackup?.sourceType === 'temporary' ? '#ecfdf5' : 'white',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      <Download size={20} color="#10b981" />
                      <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>
                        Постоянные копии
                      </h3>
                    </div>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '1rem' }}>
                      Хранятся постоянно. Удаляются только вручную.
                    </p>
                    
                    {loadingBackups ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                        Загрузка...
                      </div>
                    ) : permanentBackups.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {permanentBackups.map((backup) => (
                          <div
                            key={backup.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, backup, 'permanent')}
                            style={{
                              padding: '0.75rem',
                              background: '#f0fdf4',
                              border: '1px solid #d1fae5',
                              borderRadius: '6px',
                              cursor: 'move',
                              transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(16,185,129,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                              <div style={{ fontSize: '13px', color: '#047857', fontWeight: '500' }}>
                                {new Date(backup.created_at).toLocaleString('ru-RU')}
                              </div>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                <Button
                                  onClick={() => handleRollbackFromBackup(backup)}
                                  variant="outline"
                                  style={{
                                    fontSize: '11px',
                                    padding: '0.25rem 0.75rem',
                                    height: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem'
                                  }}
                                >
                                  <RotateCcw size={12} />
                                  Откатиться
                                </Button>
                                <Button
                                  onClick={() => handleDeletePermanentBackup(backup)}
                                  variant="outline"
                                  style={{
                                    fontSize: '11px',
                                    padding: '0.25rem 0.5rem',
                                    height: 'auto',
                                    color: '#ef4444',
                                    borderColor: '#fee2e2'
                                  }}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            </div>
                            
                            {backup.notes && (
                              <div style={{
                                padding: '0.5rem',
                                background: 'white',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#4b5563',
                                marginBottom: '0.5rem',
                                fontStyle: 'italic'
                              }}>
                                📝 {backup.notes}
                              </div>
                            )}
                            
                            <Button
                              onClick={() => handleEditNotes(backup)}
                              variant="outline"
                              style={{
                                fontSize: '11px',
                                padding: '0.25rem 0.5rem',
                                height: 'auto',
                                width: '100%'
                              }}
                            >
                              {backup.notes ? 'Редактировать заметку' : 'Добавить заметку'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontStyle: 'italic' }}>
                        Нет постоянных копий. Перетащите копию из оперативного хранилища.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>
              Не удалось загрузить настройки обновлений
            </div>
          )}

          {/* Edit Notes Modal */}
          {editingNotes && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9998
            }}>
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                maxWidth: '500px',
                width: '90%'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
                  Заметки для резервной копии
                </h2>
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '1rem' }}>
                  {editingNotes.name}
                </p>
                
                <textarea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Добавьте описание: что важного в этой копии, зачем она сохранена..."
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingNotes(null);
                      setNotesText('');
                    }}
                  >
                    Отмена
                  </Button>
                  <Button
                    onClick={handleSaveNotes}
                    style={{ background: '#10b981' }}
                  >
                    Сохранить
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Progress Modal */}
          {updatingApp && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                maxWidth: '500px',
                width: '90%'
              }}>
                <h2 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#0a4797',
                  fontSize: '24px',
                  textAlign: 'center'
                }}>
                  Обновление приложения
                </h2>
                
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '1rem'
                  }}>
                    <RefreshCw 
                      size={20} 
                      color="#0a4797"
                      style={{
                        animation: 'spin 2s linear infinite'
                      }}
                    />
                    <span style={{ 
                      fontSize: '16px', 
                      color: '#4a5568',
                      fontWeight: '500'
                    }}>
                      {updateStage}
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={{
                    width: '100%',
                    height: '12px',
                    background: '#e2e8f0',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <div style={{
                      width: `${updateProgress}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #0a4797 0%, #0e5bbd 100%)',
                      borderRadius: '6px',
                      transition: 'width 0.5s ease-in-out',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Animated shine effect */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                        animation: 'shine 2s infinite'
                      }} />
                    </div>
                  </div>
                  
                  <div style={{
                    textAlign: 'center',
                    marginTop: '0.5rem',
                    fontSize: '14px',
                    color: '#6b7280',
                    fontWeight: 'bold'
                  }}>
                    {updateProgress}%
                  </div>
                </div>
                
                <div style={{
                  padding: '1rem',
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#78350f'
                }}>
                  <strong>⚠️ Важно:</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                    <li>Не закрывайте это окно</li>
                    <li>Не обновляйте страницу</li>
                    <li>Процесс может занять до 1 минуты</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Sections Tab Content */}
      {activeTab === 'sections' && (
        <div className="admin-section">
          <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={24} />
            Разделы на главной странице
          </h2>
          
          {/* Add new section form */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
              <Plus size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Добавить раздел
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="text"
                placeholder="Название раздела"
                value={newSection.title}
                onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                style={{
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '1rem'
                }}
              />
              <textarea
                placeholder="Описание раздела (поддерживается HTML)"
                value={newSection.content}
                onChange={(e) => setNewSection({ ...newSection, content: e.target.value })}
                rows={5}
                style={{
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
              <Button onClick={createInfoSection} style={{ alignSelf: 'flex-start' }}>
                <Plus size={18} style={{ marginRight: '0.5rem' }} />
                Добавить
              </Button>
            </div>
          </div>
          
          {/* Existing sections list */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Существующие разделы</h3>
            
            {loadingSections ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Загрузка...
              </div>
            ) : infoSections.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Разделов пока нет. Добавьте первый раздел выше.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {infoSections.map((section, index) => (
                  <div
                    key={section.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1rem',
                      background: '#fafafa'
                    }}
                  >
                    {editingSection === section.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <input
                          type="text"
                          defaultValue={section.title}
                          id={`edit-title-${section.id}`}
                          style={{
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '1rem'
                          }}
                        />
                        <textarea
                          defaultValue={section.content}
                          id={`edit-content-${section.id}`}
                          rows={4}
                          style={{
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '0.9rem',
                            resize: 'vertical'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button
                            onClick={() => {
                              const title = document.getElementById(`edit-title-${section.id}`).value;
                              const content = document.getElementById(`edit-content-${section.id}`).value;
                              updateInfoSection(section.id, { title, content });
                            }}
                            size="sm"
                          >
                            Сохранить
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingSection(null)}
                          >
                            Отмена
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <GripVertical size={16} style={{ color: '#9ca3af' }} />
                            <span style={{ 
                              background: '#667eea', 
                              color: 'white', 
                              padding: '0.15rem 0.5rem', 
                              borderRadius: '4px', 
                              fontSize: '0.75rem' 
                            }}>
                              #{index + 1}
                            </span>
                            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{section.title}</h4>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingSection(section.id)}
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteInfoSection(section.id)}
                              style={{ color: '#ef4444', borderColor: '#fecaca' }}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        <div 
                          style={{ 
                            fontSize: '0.9rem', 
                            color: '#4b5563',
                            lineHeight: '1.5',
                            maxHeight: '100px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          dangerouslySetInnerHTML={{ __html: section.content.substring(0, 200) + (section.content.length > 200 ? '...' : '') }}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Objects Tab Content */}
      {activeTab === 'objects' && (
        <div className="admin-section">
          <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Layers size={24} />
            Объекты для промптов AI
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Эти объекты будут использоваться для составления промптов нейросети при добавлении объектов на панорамные фото.
          </p>
          
          {/* Add new object form */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
              <Plus size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Добавить объект
            </h3>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Текст объекта (например: красная машина, старинный фонарь)"
                value={newObjectText}
                onChange={(e) => setNewObjectText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addPromptObject()}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '1rem'
                }}
              />
              <Button onClick={addPromptObject}>
                <Plus size={18} style={{ marginRight: '0.5rem' }} />
                Добавить
              </Button>
            </div>
          </div>
          
          {/* Objects list */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
              Список объектов ({promptObjects.length})
            </h3>
            
            {loadingObjects ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Загрузка...
              </div>
            ) : promptObjects.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Объекты не добавлены
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {promptObjects.map((obj, index) => (
                  <div 
                    key={obj.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1rem',
                      background: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <span style={{ 
                      color: '#9ca3af', 
                      fontSize: '0.9rem',
                      minWidth: '24px'
                    }}>
                      {index + 1}.
                    </span>
                    
                    {editingObjectId === obj.id ? (
                      <>
                        <input
                          type="text"
                          value={editingObjectText}
                          onChange={(e) => setEditingObjectText(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && updatePromptObject(obj.id)}
                          style={{
                            flex: 1,
                            padding: '0.5rem 0.75rem',
                            borderRadius: '6px',
                            border: '1px solid #3b82f6',
                            fontSize: '1rem',
                            outline: 'none'
                          }}
                          autoFocus
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updatePromptObject(obj.id)}
                          style={{ color: '#22c55e', borderColor: '#bbf7d0' }}
                        >
                          <Save size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEditingObject}
                          style={{ color: '#6b7280', borderColor: '#e5e7eb' }}
                        >
                          <X size={14} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1, fontSize: '1rem' }}>
                          {obj.text}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEditingObject(obj)}
                          style={{ color: '#3b82f6', borderColor: '#bfdbfe' }}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deletePromptObject(obj.id)}
                          style={{ color: '#ef4444', borderColor: '#fecaca' }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* AI Prompt Template */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            marginTop: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Edit size={18} />
              Шаблон промпта для AI
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.85rem' }}>
              Этот текст будет использоваться при обработке изображений. Выбранные объекты будут добавлены автоматически.
            </p>
            <textarea
              value={aiPromptTemplate}
              onChange={(e) => setAiPromptTemplate(e.target.value)}
              style={{
                width: '100%',
                minHeight: '300px',
                padding: '1rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '0.9rem',
                fontFamily: 'monospace',
                lineHeight: '1.5',
                resize: 'vertical'
              }}
              placeholder="Введите промпт для AI..."
            />
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                Переменная {'{objects}'} будет заменена на выбранные объекты
              </span>
              <Button 
                variant="outline"
                onClick={() => {
                  setAiPromptTemplate(`Edit this section of a 360-degree panoramic photo by adding the selected objects.

CRITICAL PLACEMENT REQUIREMENTS:
1. DO NOT place any objects in the leftmost 5% of the image
2. DO NOT place any objects in the rightmost 5% of the image  
3. DO NOT place any objects in the top 5% of the image
4. DO NOT place any objects in the bottom 5% of the image
5. Place objects ONLY in the central 90% area of the image

REALISM REQUIREMENTS:
1. Objects must be placed ON THE GROUND or on realistic surfaces (tables, shelves, floor)
2. Objects must have correct proportions relative to the scene
3. Match the lighting, shadows, and perspective of the original scene
4. Objects should look natural and belong in the scene
5. Keep the original background completely intact
6. Make objects clearly visible and properly sized for the scene`);
                }}
              >
                Сбросить
              </Button>
              <Button onClick={saveAiPromptTemplate}>
                <Save size={14} style={{ marginRight: '0.5rem' }} />
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Video Analytics (combined with Monitoring) */}
      {activeTab === 'analytics' && (
        <VideoAnalytics games={analyticsGames} panoramas={analyticsPanoramas} />
      )}

      {/* Monitoring section (combined with Analytics) */}
      {activeTab === 'analytics' && (
        <div style={{ padding: '1rem 0' }}>
          {monitorLoading && !monitorData ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Загрузка...</div>
          ) : monitorData ? (
            <>
              {/* Alerts */}
              {monitorData.alerts && monitorData.alerts.length > 0 && (
                <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {monitorData.alerts.map((a, i) => (
                    <div key={i} style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      background: a.level === 'critical' ? '#fef2f2' : '#fff7ed',
                      border: `1px solid ${a.level === 'critical' ? '#fecaca' : '#fed7aa'}`,
                      color: a.level === 'critical' ? '#dc2626' : '#c2410c',
                      fontWeight: 600,
                      fontSize: '0.85rem'
                    }}>
                      {a.level === 'critical' ? '!!! ' : '! '}{a.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Main metrics cards - now shown as individual charts below */}
              <div style={{ display: 'none' }}>
                {/* Network OUT */}
                <div style={{ background: '#f0f9ff', borderRadius: '12px', padding: '1rem', border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: '0.75rem', color: '#0369a1', marginBottom: '4px' }}>Исходящий трафик</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#0c4a6e' }}>{monitorData.network?.out_mbps || 0}</div>
                  <div style={{ fontSize: '0.7rem', color: '#0369a1' }}>Мбит/с из {monitorData.network?.max_bandwidth_mbps || bandwidthSetting} Мбит</div>
                  <div style={{ marginTop: '6px', height: '6px', borderRadius: '3px', background: '#e0f2fe', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: '3px', width: `${Math.min(100, monitorData.network?.bandwidth_usage_pct || 0)}%`, background: (monitorData.network?.bandwidth_usage_pct || 0) > 70 ? '#dc2626' : (monitorData.network?.bandwidth_usage_pct || 0) > 50 ? '#f59e0b' : '#22c55e', transition: 'width 0.5s' }} />
                  </div>
                </div>

                {/* Network IN */}
                <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '1rem', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#15803d', marginBottom: '4px' }}>Входящий трафик</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#14532d' }}>{monitorData.network?.in_mbps || 0}</div>
                  <div style={{ fontSize: '0.7rem', color: '#15803d' }}>Мбит/с</div>
                </div>

                {/* Estimated streams */}
                <div style={{ background: '#faf5ff', borderRadius: '12px', padding: '1rem', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '0.75rem', color: '#7c3aed', marginBottom: '4px' }}>Видео потоков (1080p)</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#4c1d95' }}>~{monitorData.connections?.estimated_1080p_streams || 0}</div>
                  <div style={{ fontSize: '0.7rem', color: '#7c3aed' }}>оценка по трафику</div>
                </div>

                {/* Connections */}
                <div style={{ background: '#fff7ed', borderRadius: '12px', padding: '1rem', border: '1px solid #fed7aa' }}>
                  <div style={{ fontSize: '0.75rem', color: '#c2410c', marginBottom: '4px' }}>HTTP соединений</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#9a3412' }}>{monitorData.connections?.http_active || 0}</div>
                  <div style={{ fontSize: '0.7rem', color: '#c2410c' }}>активных / {monitorData.connections?.established || 0} всего</div>
                </div>

                {/* CPU */}
                <div style={{ background: '#fefce8', borderRadius: '12px', padding: '1rem', border: '1px solid #fef08a' }}>
                  <div style={{ fontSize: '0.75rem', color: '#a16207', marginBottom: '4px' }}>CPU ({monitorData.cpu?.cores} ядер)</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#713f12' }}>{monitorData.cpu?.total_percent || 0}%</div>
                  <div style={{ fontSize: '0.7rem', color: '#a16207' }}>load: {monitorData.cpu?.load_avg_1m} / {monitorData.cpu?.load_avg_5m} / {monitorData.cpu?.load_avg_15m}</div>
                  <div style={{ marginTop: '6px', height: '6px', borderRadius: '3px', background: '#fef9c3', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: '3px', width: `${monitorData.cpu?.total_percent || 0}%`, background: (monitorData.cpu?.total_percent || 0) > 85 ? '#dc2626' : '#eab308', transition: 'width 0.5s' }} />
                  </div>
                </div>

                {/* RAM */}
                <div style={{ background: '#fdf2f8', borderRadius: '12px', padding: '1rem', border: '1px solid #fbcfe8' }}>
                  <div style={{ fontSize: '0.75rem', color: '#be185d', marginBottom: '4px' }}>RAM</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#831843' }}>{monitorData.memory?.used_gb || 0} ГБ</div>
                  <div style={{ fontSize: '0.7rem', color: '#be185d' }}>из {monitorData.memory?.total_gb || 0} ГБ ({monitorData.memory?.percent || 0}%)</div>
                  <div style={{ marginTop: '6px', height: '6px', borderRadius: '3px', background: '#fce7f3', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: '3px', width: `${monitorData.memory?.percent || 0}%`, background: (monitorData.memory?.percent || 0) > 90 ? '#dc2626' : '#ec4899', transition: 'width 0.5s' }} />
                  </div>
                </div>

                {/* Data disk */}
                <div style={{ background: monitorData.disk?.percent > 80 ? '#fef2f2' : '#f8fafc', borderRadius: '12px', padding: '1rem', border: `1px solid ${monitorData.disk?.percent > 80 ? '#fecaca' : '#e2e8f0'}` }}>
                  <div style={{ fontSize: '0.75rem', color: monitorData.disk?.percent > 80 ? '#dc2626' : '#64748b', marginBottom: '4px' }}>Диск данных (/var)</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: monitorData.disk?.percent > 80 ? '#991b1b' : '#1e293b' }}>{monitorData.disk?.percent || 0}%</div>
                  <div style={{ fontSize: '0.7rem', color: monitorData.disk?.percent > 80 ? '#dc2626' : '#64748b' }}>Диск загружен • свободно {monitorData.disk?.free_gb || 0} из {monitorData.disk?.total_gb || 0} ГБ</div>
                  <div style={{ marginTop: '6px', height: '6px', borderRadius: '3px', background: '#f1f5f9', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: '3px', width: `${monitorData.disk?.percent || 0}%`, background: (monitorData.disk?.percent || 0) > 90 ? '#dc2626' : (monitorData.disk?.percent || 0) > 80 ? '#f59e0b' : '#94a3b8', transition: 'width 0.5s' }} />
                  </div>
                </div>
                {/* System disk */}
                {monitorData.disk_system && (
                <div style={{ background: monitorData.disk_system?.percent > 80 ? '#fef2f2' : '#f8fafc', borderRadius: '12px', padding: '1rem', border: `1px solid ${monitorData.disk_system?.percent > 80 ? '#fecaca' : '#e2e8f0'}` }}>
                  <div style={{ fontSize: '0.75rem', color: monitorData.disk_system?.percent > 80 ? '#dc2626' : '#64748b', marginBottom: '4px' }}>Диск системы (/)</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: monitorData.disk_system?.percent > 80 ? '#991b1b' : '#1e293b' }}>{monitorData.disk_system?.percent || 0}%</div>
                  <div style={{ fontSize: '0.7rem', color: monitorData.disk_system?.percent > 80 ? '#dc2626' : '#64748b' }}>Диск загружен • свободно {monitorData.disk_system?.free_gb || 0} из {monitorData.disk_system?.total_gb || 0} ГБ</div>
                  <div style={{ marginTop: '6px', height: '6px', borderRadius: '3px', background: '#f1f5f9', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: '3px', width: `${monitorData.disk_system?.percent || 0}%`, background: (monitorData.disk_system?.percent || 0) > 90 ? '#dc2626' : (monitorData.disk_system?.percent || 0) > 80 ? '#f59e0b' : '#94a3b8', transition: 'width 0.5s' }} />
                  </div>
                </div>
                )}
              </div>

              {/* Bandwidth setting */}
              <div style={{ background: '#fff', borderRadius: '12px', padding: '1rem', border: '1px solid #e2e8f0', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 500 }}>Скорость интернет-канала:</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="number"
                    value={bandwidthInput}
                    onChange={e => setBandwidthInput(e.target.value)}
                    style={{ width: '100px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.9rem', fontWeight: 600, textAlign: 'right' }}
                  />
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Мбит/с</span>
                  <button
                    disabled={bandwidthSaving || String(bandwidthSetting) === bandwidthInput}
                    onClick={() => {
                      setBandwidthSaving(true);
                      axios.post(`${API}/admin/monitoring/bandwidth-setting`, { max_bandwidth_mbps: parseFloat(bandwidthInput) }, { headers: getAuthHeader() })
                        .then(res => {
                          setBandwidthSetting(res.data.max_bandwidth_mbps);
                          toast.success('Скорость канала обновлена');
                        })
                        .catch(() => toast.error('Ошибка сохранения'))
                        .finally(() => setBandwidthSaving(false));
                    }}
                    style={{
                      padding: '6px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                      fontSize: '0.8rem', fontWeight: 600,
                      background: String(bandwidthSetting) === bandwidthInput ? '#e2e8f0' : '#0c4a6e',
                      color: String(bandwidthSetting) === bandwidthInput ? '#94a3b8' : '#fff',
                      opacity: bandwidthSaving ? 0.6 : 1
                    }}
                  >
                    {bandwidthSaving ? '...' : 'Сохранить'}
                  </button>
                </div>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Используется для расчёта % загрузки канала и алертов</span>
              </div>

              {/* History chart section */}
              <div style={{ background: '#fff', borderRadius: '12px', padding: '1.25rem', border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>История нагрузки</h3>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {['1h', '24h', '7d', '30d'].map(p => (
                      <button key={p} onClick={() => {
                        setMonitorPeriod(p);
                        axios.get(`${API}/admin/monitoring/history?period=${p}`, { headers: getAuthHeader() })
                          .then(res => setMonitorHistory(res.data?.data || []))
                          .catch(() => {});
                      }} style={{
                        padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                        fontSize: '0.75rem', fontWeight: 600,
                        background: monitorPeriod === p ? '#0c4a6e' : '#f1f5f9',
                        color: monitorPeriod === p ? '#fff' : '#64748b'
                      }}>{p}</button>
                    ))}
                  </div>
                </div>

                <MonitorChart data={monitorHistory} period={monitorPeriod} monitorData={monitorData} bandwidthMbps={bandwidthSetting} />
              </div>

              <div style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'right' }}>
                Обновление каждые 10 сек | Запись в историю каждую минуту | Хранение: 7 дней (детально) / 90 дней (почасово)
              </div>
            </>
          ) : null}
        </div>
      )}

            {activeTab === 'antifraud' && (
        <div style={{ padding: '1rem 0' }}>
          {antifraudLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Загрузка...</div>
          ) : (
            <>
              {/* Stats cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ background: '#f0f9ff', borderRadius: '12px', padding: '1.25rem', border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: '0.85rem', color: '#0369a1' }}>Отпечатков собрано</div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#0c4a6e' }}>{antifraudStats?.total_fingerprints || 0}</div>
                </div>
                <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '1.25rem', border: '1px solid #fecaca' }}>
                  <div style={{ fontSize: '0.85rem', color: '#dc2626' }}>Подозрительных групп</div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#991b1b' }}>{antifraudStats?.suspect_groups || 0}</div>
                </div>
                <div style={{ background: '#fff7ed', borderRadius: '12px', padding: '1.25rem', border: '1px solid #fed7aa' }}>
                  <div style={{ fontSize: '0.85rem', color: '#c2410c' }}>Замороженных ответов</div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#9a3412' }}>{antifraudStats?.total_frozen_answers || 0}</div>
                </div>
                <div style={{ background: '#fdf2f8', borderRadius: '12px', padding: '1.25rem', border: '1px solid #fbcfe8' }}>
                  <div style={{ fontSize: '0.85rem', color: '#be185d' }}>Подозрительных приглашений</div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#9d174d' }}>{antifraudReferralsTotal}</div>
                </div>
              </div>

              {/* Suspect groups */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem', color: '#1e293b' }}>Подозрительные аккаунты</h3>
              {antifraudSuspects.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '8px' }}>
                  Совпадений не обнаружено
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  {antifraudSuspects.map((group, idx) => (
                    <div key={idx} style={{ background: '#fff', borderRadius: '10px', padding: '1rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: '#64748b' }}>
                          <span>IP: <strong style={{color:'#dc2626'}}>{group.ip}</strong></span>
                          <span>FP: <code style={{fontSize:'0.75rem', background:'#f1f5f9', padding:'2px 6px', borderRadius:'4px'}}>{group.fingerprint_short}</code></span>
                        </div>
                        <span style={{ fontSize: '0.75rem', background: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                          {group.user_count} аккаунтов
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {group.users.map((u, i) => (
                          <div key={i} style={{ fontSize: '0.8rem', background: '#f8fafc', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            {u.full_name || u.email} <span style={{color:'#94a3b8'}}>({u.role})</span>
                          </div>
                        ))}
                      </div>
                      {group.frozen_answers > 0 && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#ea580c' }}>
                          Заморожено ответов: {group.frozen_answers}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Suspicious referral invites */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem', color: '#1e293b' }}>
                Подозрительные реферальные приглашения{' '}
                {antifraudReferralsTotal > 0 && (
                  <span style={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 700 }}>
                    ({antifraudReferralsTotal})
                  </span>
                )}
              </h3>
              {antifraudReferrals.length === 0 ? (
                <div data-testid="antifraud-referrals-empty" style={{ padding: '2rem', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '8px', marginBottom: '1.5rem' }}>
                  Подозрительных приглашений не обнаружено
                </div>
              ) : (
                <div data-testid="antifraud-referrals-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.5rem' }}>
                  {antifraudReferrals.map((ev) => {
                    const key = `${ev.link_id}|${ev.user_id}`;
                    const busy = unblockingRef === key;
                    return (
                      <div
                        key={key}
                        data-testid={`antifraud-ref-event-${ev.user_id}`}
                        style={{
                          background: '#fffbfb', borderRadius: '10px', padding: '0.9rem 1rem',
                          border: '1px solid #fecaca', display: 'grid',
                          gridTemplateColumns: '1.2fr 1.2fr 1fr auto',
                          gap: '0.75rem', alignItems: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 2 }}>Приглашённый</div>
                          <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.85rem' }}>
                            {ev.new_user?.full_name || ev.new_user?.email || '—'}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#6b7280', wordBreak: 'break-all' }}>
                            {ev.new_user?.email}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>
                            {ev.ts ? new Date(ev.ts).toLocaleString('ru') : ''}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 2 }}>Отправитель</div>
                          <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.85rem' }}>
                            {ev.sender?.full_name || ev.sender?.email || '—'}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#6b7280', wordBreak: 'break-all' }}>
                            {ev.sender?.email}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#0a4797', fontFamily: 'monospace', marginTop: 2 }}>
                            /ref/{ev.link_id}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignContent: 'flex-start' }}>
                          {(ev.flagged_reasons || []).map(r => (
                            <span key={r} style={{
                              fontSize: '0.7rem', padding: '2px 7px', borderRadius: 999,
                              background: '#fef2f2', color: '#b91c1c',
                              border: '1px solid #fecaca', fontWeight: 600,
                            }}>{r}</span>
                          ))}
                          {ev.new_user?.signup_ip && (
                            <span title="IP при регистрации" style={{ fontSize: '0.68rem', color: '#6b7280', fontFamily: 'monospace', padding: '2px 6px', background: '#f3f4f6', borderRadius: 4 }}>
                              {ev.new_user.signup_ip}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleUnblockReferral(ev.link_id, ev.user_id)}
                          disabled={busy}
                          data-testid={`antifraud-ref-unblock-${ev.user_id}`}
                          title="Снять флаг и начислить отправителю бонус ретроактивно"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '8px 12px', border: '1px solid #bbf7d0',
                            background: busy ? '#f0fdf4' : '#ecfdf5', color: '#166534',
                            borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
                            fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
                          }}
                        >
                          <Unlock size={13} />
                          {busy ? 'Разблокируем…' : 'Разблокировать вручную'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recent log */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem', color: '#1e293b' }}>Лог замороженных ответов</h3>
              {antifraudLog.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '8px' }}>
                  Замороженных ответов пока нет
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Дата</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Пользователь</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Панорама</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>IP</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Связанные</th>
                      </tr>
                    </thead>
                    <tbody>
                      {antifraudLog.map((log, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 12px', color: '#64748b' }}>{log.detected_at ? new Date(log.detected_at).toLocaleString('ru') : '-'}</td>
                          <td style={{ padding: '8px 12px' }}>{log.user_info?.full_name || log.user_info?.email || log.user_id}</td>
                          <td style={{ padding: '8px 12px' }}>{log.panorama_name || log.panorama_id?.slice(0,8)}</td>
                          <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{log.ip}</td>
                          <td style={{ padding: '8px 12px', color: '#dc2626' }}>{log.matched_users?.length || 0} акк.</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'settings' && <AdminGeneralSettings />}
    </div>
  );
};

export default AdminPanel;
